import { useMailProfile } from './viewmodels/useMailProfile';
import { useMailLabels } from './viewmodels/useMailLabels';
import { useLocalDraft } from './viewmodels/useLocalDraft';
import { useMailNotifications } from './viewmodels/useMailNotifications';
import { notificationDesign } from '../../utils/notificationDesign';
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { withNotificationActions } from '../../utils/actionableNotifications';
import { App, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../../features/access/context/AccessProvider';
import { useSocket } from '../contexts/SocketContext';
import { draftKey } from './mailboxModel';
import MailComposer from './MailComposer';
import MailSenderAvatar from './views/MailSenderAvatar';
import './mailbox.css';

const Context = createContext(null);
export const useMailbox = () => useContext(Context);
export function MailboxProvider({ children }) {
  const { authState } = useAuth();
  const { can, status } = useAccess();
  const enabled = !!authState?.isAuthenticated && status === 'ready' && can('mailbox.view');
  const identity = authState?.isAuthenticated ? String(authState.id) : '';
  // Remount all mailbox state on identity/access changes, including pending requests.
  return <MailboxSession key={identity} enabled={enabled} userId={identity}>{children}</MailboxSession>;
}
function presentMailNotification(row, acknowledge, notification, navigate) {
  notification.info({
    ...notificationDesign('mail', 'info'), key: `mail-${row.id}`, message: row.from || 'New email', description: row.subject || '(No subject)', duration: 0, placement: 'topRight',
    icon: <MailSenderAvatar from={row.from} image={row.sender_image} />,
    onClose: () => acknowledge(row.id),
    actions: <Button size="small" onClick={() => { navigate(`/mail/${row.gmail_thread_id}`); acknowledge(row.id); notification.destroy(`mail-${row.id}`); }}>Open email</Button> });
}
function MailboxSession({ enabled, userId, children }) {
  const { can } = useAccess();
  const { socket } = useSocket();
  const { notification: rawNotification, message } = App.useApp();
  const notification = useMemo(() => withNotificationActions(rawNotification), [rawNotification]);
  const navigate = useNavigate();
  const location = useLocation();
  const live = useRef(true);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  // Profile initialization loads the draft in the same completion as before;
  // the ref connects the two hooks without duplicating either state owner.
  const draftLoader = useRef(null);
  const loadDraft = useCallback((id, email) => draftLoader.current(id, email), []);
  const { profile, error, loading, initialize } = useMailProfile(enabled, userId, live, loadDraft);
  const key = profile ? draftKey(userId, profile.primary_email) : null;
  const { draft, setDraft, storageError, loadDraft: initializeDraft, compose, clearDraft, persistCommand } = useLocalDraft(key, { profile, canSend: can('mailbox.send'), notify: message });
  draftLoader.current = initializeDraft;
  const { labels, labelsError, refreshLabels, inboxUnread } = useMailLabels(enabled, profile, revision, socket);
  const onArrival = useCallback((row, acknowledge) => presentMailNotification(row, acknowledge, notification, navigate), [notification, navigate]);
  const destroy = useCallback(key => notification.destroy(key), [notification]);
  const { notifications, acknowledge } = useMailNotifications({ enabled, profile, socket, live, refresh, pathname: location.pathname, onArrival, destroy, notify: message });
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const value = { labels, labelsError, refreshLabels, inboxUnread, profile, error, loading, initialize, draft, setDraft, compose, clearDraft, persistCommand, storageError, notifications, acknowledge, revision, refresh };
  return <Context.Provider value={value}>{children}{enabled && profile && can('mailbox.send') && draft && !draft.closed && <MailComposer />}</Context.Provider>;
}
